"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useAdminOverlay } from "./admin-overlay-context";
import { getAdminSection, type AdminSectionKey } from "./section-generators";

// Régénère une section : appelle la route `generate-*` existante avec le
// prompt (édité ou par défaut), puis PATCH `content_items.data` avec l'état
// COMPLET (état courant + le seul champ regénéré) — jamais un objet partiel :
// le schéma zod de la route PATCH remplit de force les champs absents avec
// leurs défauts (chaîne vide, null...), donc un PATCH partiel écraserait
// silencieusement tout le reste de l'offre. `router.refresh()` fait
// redescendre la nouvelle donnée depuis le serveur pour toute la page.
export function useSectionRegenerate(key: AdminSectionKey) {
  const router = useRouter();
  const { itemId, data, publicUrl } = useAdminOverlay();
  const descriptor = getAdminSection(key);
  const [isRunning, setIsRunning] = useState(false);

  const run = async (prompt: string): Promise<boolean> => {
    setIsRunning(true);
    try {
      const body = descriptor.buildBody(prompt, { data, publicUrl });
      const res = await fetch(descriptor.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.error) {
        throw new Error(json?.error || "Échec de la génération");
      }

      const patch = descriptor.applyResult(json);
      const merged = { ...data, ...patch };
      const patchRes = await fetch(`/api/super-admin/content/${itemId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: merged }),
      });
      const patchJson = await patchRes.json().catch(() => ({}));
      if (!patchRes.ok || patchJson?.error) {
        throw new Error(patchJson?.error || "Échec de l'enregistrement");
      }

      toast.success(`${descriptor.label} régénéré`);
      router.refresh();
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de la génération");
      return false;
    } finally {
      setIsRunning(false);
    }
  };

  return { descriptor, isRunning, run };
}
